import { FieldHint } from "@/components/field-hint/FieldHint.tsx";
import { Datepicker } from "@/components/datepicker/Datepicker.tsx";
import { CurrencySelect } from "@/components/select/CurrencySelect.tsx";
import { parseSalaryValue } from "@/helpers/utils.helper.ts";
import { ServerErrorField } from "@/components/server-error-field/ServerErrorField.tsx";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import useCustomFieldsHook from "@/hooks/use-custom-fields.hook.tsx";
import {
  useEntityReferenceOptions,
  useEntityReferenceValues,
} from "@/hooks/use-entity-reference-options.hook.ts";
import { useSystemTableConfig } from "@/hooks/use-system-table-config.hook.ts";
import { ChangesPreviewItem } from "@/pages/employees/employee/components/ChangesPreview.tsx";
import { EmployeeCompensationLines } from "@/pages/employees/employee/components/EmployeeCompensationLines.tsx";
import { EmployeeCompensationPreview } from "@/pages/employees/employee/components/EmployeeCompensationPreview.tsx";
import {
  EmployeeCompensationFormValues,
  EmployeeCompensationLineFormValue,
} from "@/pages/employees/employee/components/employee-compensation-form.types.ts";
import { diffCompensationLines } from "@/pages/employees/employee/widgets/compensation/compensation-package-diff.ts";
import { useCompensationComponents } from "@/queries/use-compensation-components.query.ts";
import {
  useEmployeeCompensationCurrent,
  useEmployeeCompensationPreview,
  useEmployeeCompensationSave,
} from "@/queries/employees/use-employee-compensations.query.ts";
import { useEmployeeMe } from "@/queries/employees/use-employees.query.ts";
import { useCompensationChangeReasons } from "@/queries/use-compensation-change-reasons.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { BASIC_SALARY_COMPENSATION_COMPONENT_CODE } from "@/types/compensation-component.types.ts";
import {
  ApiEmployeeCompensation,
  ApiEmployeeCompensationPerTypes,
  CompensationLineSave,
  CompensationPackageSave,
} from "@/types/employees/employee-compensation.types.ts";
import { ApiEmployee } from "@/types/employees/employee.types.ts";
import { ApiFieldTypes, ApiTableTypes } from "@/types/custom-field.types.ts";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Button,
  Modal,
  ModalProps,
  Select,
  Skeleton,
  Stack,
  Textarea,
  useModals,
} from "@skailer-com/uikit";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Controller, Resolver, useFieldArray, useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

export interface EmployeeCompensationEditModalProps extends ModalProps {
  employee: ApiEmployee;
  employeeCompensation?: ApiEmployeeCompensation;
  lastCurrency?: ApiEmployeeCompensation["currency"];
}

const amountToNumber = (value: string | number | null) => {
  const parsed = parseSalaryValue(value);
  return typeof parsed === "number" && !Number.isNaN(parsed) ? parsed : null;
};

export function EmployeeCompensationEditModal({
  employee,
  employeeCompensation,
  lastCurrency,
  ...props
}: EmployeeCompensationEditModalProps) {
  const { t } = useTranslation();
  const { close } = useModals();
  const { renderEmployeeName, renderDate } = useFormatter();
  const { data: employeeMe } = useEmployeeMe();
  const {
    fields: configFields,
    customFields,
    isMandatory,
  } = useSystemTableConfig(ApiTableTypes.employee_compensations);
  const {
    getFieldByType,
    getValueForFieldFromValueObject,
    getCustomFieldPreviewValue,
    getZodSchemaForField,
    transformFormDataToValue,
  } = useCustomFieldsHook();
  const fieldConfig = useCallback(
    (type: ApiFieldTypes) => configFields.find((field) => field.type === type),
    [configFields]
  );
  const fieldOrder = useCallback(
    (type: ApiFieldTypes) => configFields.findIndex((field) => field.type === type),
    [configFields]
  );
  const fieldTitle = (type: ApiFieldTypes, fallback: string) =>
    fieldConfig(type)?.title?.trim() || fallback;
  const { data: components } = useCompensationComponents();
  const { data: changeReasons, isLoading: changeReasonsLoading } = useCompensationChangeReasons();
  const save = useEmployeeCompensationSave();
  const preview = useEmployeeCompensationPreview();
  const { data: currentCompensation, isLoading: currentCompensationLoading } =
    useEmployeeCompensationCurrent(employee.id, !employeeCompensation);
  const [isReviewing, setIsReviewing] = useState(false);
  const sourceCompensation = employeeCompensation ?? currentCompensation ?? undefined;
  const defaultCurrency =
    sourceCompensation?.currency ?? lastCurrency ?? employeeMe?.organization.currency ?? "";
  const amountSchema = z.union([z.string(), z.number()]).nullable();
  const lineSchema = z
    .object({
      id: z.number().optional(),
      compensation_component_id: z
        .number({ error: t("errors:field_required") })
        .positive(t("errors:field_required")),
      amount_gross: amountSchema,
      amount_net: amountSchema,
      currency_code: z.string().nullable(),
      per_type: z.enum(ApiEmployeeCompensationPerTypes).nullable(),
      sort: z.number().nullable(),
      description: z.string().nullable(),
    })
    .superRefine((line, context) => {
      (["amount_gross", "amount_net"] as const).forEach((field) => {
        const value = line[field];
        if (value !== null && value !== "" && amountToNumber(value) === null) {
          context.addIssue({
            code: "custom",
            message: t("errors:field_number_required"),
            path: [field],
          });
        }
      });
      if (
        (line.amount_gross === null || line.amount_gross === "") &&
        (line.amount_net === null || line.amount_net === "")
      ) {
        (["amount_gross", "amount_net"] as const).forEach((field) =>
          context.addIssue({
            code: "custom",
            message: t("errors:field_required"),
            path: [field],
          })
        );
      }
      if (!line.currency_code) {
        context.addIssue({
          code: "custom",
          message: t("errors:field_required"),
          path: ["currency_code"],
        });
      }
      if (!line.per_type) {
        context.addIssue({
          code: "custom",
          message: t("errors:field_required"),
          path: ["per_type"],
        });
      }
    }) satisfies z.ZodType<EmployeeCompensationLineFormValue>;
  const formSchema = z.object({
    date_effective_from: z.date({ error: t("errors:field_required") }),
    currency: z.string({ error: t("errors:field_required") }).min(1, t("errors:field_required")),
    change_reason_id: isMandatory(ApiFieldTypes.employee_compensation_change_reason)
      ? z.number({ error: t("errors:field_required") })
      : z.number().nullable().optional(),
    comment: isMandatory(ApiFieldTypes.employee_compensation_comment)
      ? z.string({ error: t("errors:field_required") }).min(1, t("errors:field_required"))
      : z.string().nullable().optional(),
    lines: z.array(lineSchema),
    ...Object.assign({}, ...customFields.map((field) => getZodSchemaForField(field))),
  });
  const originalLines = useMemo<EmployeeCompensationFormValues["lines"]>(
    () =>
      sourceCompensation?.lines?.map((line) => ({
        id: line.id,
        compensation_component_id: line.compensation_component_id,
        amount_gross: line.amount_gross === null ? null : Number(line.amount_gross),
        amount_net: line.amount_net === null ? null : Number(line.amount_net),
        currency_code: line.currency_code,
        per_type: line.per_type,
        sort: line.sort,
        description: line.description,
      })) ?? [],
    [sourceCompensation]
  );
  const form = useForm<EmployeeCompensationFormValues>({
    resolver: zodResolver(formSchema) as unknown as Resolver<EmployeeCompensationFormValues>,
    defaultValues: {
      date_effective_from: employeeCompensation?.date_effective_from ?? new Date(),
      currency: defaultCurrency,
      change_reason_id: employeeCompensation?.change_reason?.id ?? null,
      comment: employeeCompensation?.comment ?? null,
      lines: originalLines,
      ...Object.assign(
        {},
        ...customFields.map((field) => {
          const value = sourceCompensation?.custom_fields?.find(
            (item) => item.field.id === field.id
          );
          return getValueForFieldFromValueObject(field, value?.value, true);
        })
      ),
    },
  });
  const {
    control,
    getValues,
    handleSubmit,
    register,
    reset,
    setValue,
    trigger,
    formState: { errors },
  } = form;
  const referenceValues = useEntityReferenceValues(customFields, control);
  const { optionsByEntity: referenceOptionsByEntity } = useEntityReferenceOptions(
    customFields,
    referenceValues
  );
  const { fields, append, remove } = useFieldArray({
    control,
    name: "lines",
    keyName: "fieldKey",
  });
  const watched = useWatch({ control });

  useEffect(() => {
    if (employeeCompensation || !currentCompensation) return;
    reset({
      date_effective_from: new Date(),
      currency: defaultCurrency,
      change_reason_id: null,
      comment: null,
      lines: originalLines,
      ...Object.assign(
        {},
        ...customFields.map((field) => {
          const value = currentCompensation.custom_fields?.find(
            (item) => item.field.id === field.id
          );
          return getValueForFieldFromValueObject(field, value?.value, true);
        })
      ),
    });
    // useCustomFieldsHook returns render helpers with unstable references.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentCompensation,
    customFields,
    defaultCurrency,
    employeeCompensation,
    originalLines,
    reset,
  ]);

  useEffect(() => {
    if (!defaultCurrency || getValues("currency")) return;
    setValue("currency", defaultCurrency);
  }, [defaultCurrency, getValues, setValue]);

  useEffect(() => {
    if (!employeeCompensation) return;

    customFields.forEach((field) => {
      if (getValues(field.slug) !== undefined) return;
      const value = employeeCompensation.custom_fields?.find((item) => item.field.id === field.id);
      const fieldValues = getValueForFieldFromValueObject(field, value?.value, true);
      Object.entries(fieldValues).forEach(([key, fieldValue]) =>
        setValue(key, fieldValue, { shouldDirty: false })
      );
    });
    // useCustomFieldsHook returns render helpers with unstable references.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customFields, employeeCompensation, getValues, setValue]);

  useEffect(() => {
    const basicSalaryComponent = components?.find(
      (component) => component.code === BASIC_SALARY_COMPENSATION_COMPONENT_CODE
    );
    if (
      !basicSalaryComponent ||
      fields.length > 0 ||
      (!employeeCompensation && Boolean(currentCompensation)) ||
      (!employeeCompensation && currentCompensationLoading)
    )
      return;

    append({
      compensation_component_id: basicSalaryComponent.id,
      amount_gross: null,
      amount_net: null,
      currency_code: basicSalaryComponent.default_currency_code ?? defaultCurrency,
      per_type: basicSalaryComponent.default_per_type ?? ApiEmployeeCompensationPerTypes.monthly,
      sort: basicSalaryComponent.sort,
      description: null,
    });
  }, [
    append,
    components,
    currentCompensation,
    currentCompensationLoading,
    defaultCurrency,
    employeeCompensation,
    fields.length,
  ]);

  const normalizeLines = useCallback(
    (lines: EmployeeCompensationFormValues["lines"], keepIds: boolean): CompensationLineSave[] =>
      lines.map((line) => ({
        ...line,
        id: keepIds ? line.id : undefined,
        amount_gross: amountToNumber(line.amount_gross),
        amount_net: amountToNumber(line.amount_net),
        description: line.description?.trim() || null,
      })),
    []
  );

  const toPayload = useCallback(
    (data: EmployeeCompensationFormValues): CompensationPackageSave => ({
      employee_id: employee.id,
      date_effective_from: data.date_effective_from,
      currency: data.currency,
      change_reason_id: data.change_reason_id ?? null,
      comment: data.comment?.trim() || null,
      lines: normalizeLines(data.lines, Boolean(employeeCompensation)),
      custom_fields: Object.fromEntries(
        customFields.map((field) => [field.slug, transformFormDataToValue(field, data)])
      ),
    }),
    [customFields, employee.id, employeeCompensation, normalizeLines, transformFormDataToValue]
  );

  const review = handleSubmit(async (data) => {
    try {
      await preview.mutateAsync({ payload: toPayload(data) });
      setIsReviewing(true);
    } catch {}
  });

  const submit = handleSubmit(async (data) => {
    try {
      await save.mutateAsync({
        employeeId: employee.id,
        employeeCompensationId: employeeCompensation?.id,
        payload: toPayload(data),
      });
      analyticsService.trackEvent(
        employeeCompensation
          ? analyticEvents.employeeCompensations.edited
          : analyticEvents.employeeCompensations.created,
        { [analyticProperties.id]: employee.id }
      );
      close(props.id);
    } catch {}
  });

  const currentLines = isReviewing ? normalizeLines(getValues("lines"), true) : [];
  const diffs = diffCompensationLines(originalLines as CompensationLineSave[], currentLines);
  const previewValues = getValues();
  const changeItems: ChangesPreviewItem[] = [
    {
      id: "date_effective_from",
      title: t("common:date_effective_from"),
      currentValue: sourceCompensation?.id
        ? renderDate(sourceCompensation.date_effective_from)
        : "",
      newValue: renderDate(previewValues.date_effective_from),
    },
    {
      id: "currency",
      title: t("common:currency"),
      currentValue: sourceCompensation?.currency ?? "",
      newValue: previewValues.currency,
    },
    {
      id: "change_reason_id",
      title: t("core:compensation_change_reason"),
      currentValue: sourceCompensation?.change_reason?.title ?? "",
      newValue:
        changeReasons?.find((reason) => reason.id === previewValues.change_reason_id)?.title ?? "",
    },
    {
      id: "comment",
      title: t("common:comment"),
      currentValue: sourceCompensation?.comment ?? "",
      newValue: previewValues.comment?.trim() ?? "",
    },
    ...customFields.map((field) => {
      const customField = sourceCompensation?.custom_fields?.find(
        (item) => item.field.id === field.id
      );
      const currentFieldData = getValueForFieldFromValueObject(field, customField?.value, true);
      return {
        id: field.slug,
        title: field.title,
        currentValue: getCustomFieldPreviewValue(
          field,
          currentFieldData,
          customField?.value?.value_references ?? []
        ),
        newValue: getCustomFieldPreviewValue(
          field,
          previewValues,
          field.reference_entity ? (referenceOptionsByEntity[field.reference_entity] ?? []) : []
        ),
      };
    }),
  ];
  const missingPositions = new Set([
    ...(preview.data?.missing_fx_line_ids ?? []),
    ...(preview.data?.missing_amount_line_ids ?? []),
  ]);
  const missingTitles = [...missingPositions].map((position) => {
    const line = getValues("lines")[position - 1];
    return (
      components?.find((component) => component.id === line?.compensation_component_id)?.title ??
      String(position)
    );
  });

  const addLine = () =>
    append({
      compensation_component_id: 0,
      amount_gross: null,
      amount_net: null,
      currency_code: defaultCurrency,
      per_type: ApiEmployeeCompensationPerTypes.monthly,
      sort: (fields.length + 1) * 10,
      description: null,
    });

  const changeLineComponent = (index: number, componentId: number) => {
    const component = components?.find((item) => item.id === componentId);
    if (!component) return;
    setValue(`lines.${index}.currency_code`, component.default_currency_code ?? defaultCurrency, {
      shouldDirty: true,
      shouldValidate: true,
    });
    setValue(
      `lines.${index}.per_type`,
      component.default_per_type ?? ApiEmployeeCompensationPerTypes.monthly,
      { shouldDirty: true, shouldValidate: true }
    );
    setValue(`lines.${index}.sort`, component.sort, { shouldDirty: true });
  };

  console.log("errors", errors);

  return (
    <Modal
      {...props}
      onBack={isReviewing ? () => setIsReviewing(false) : undefined}
      title={isReviewing ? t("core:preview_changes") : t("core:compensation")}
      caption={renderEmployeeName(employee)}
      variant="side"
      layout="stretch"
      withCloser
      actions={
        <Stack gap="sm">
          <ServerErrorField errors={errors} />
          <Button
            size="lg"
            disabled={currentCompensationLoading}
            isLoading={
              currentCompensationLoading || (isReviewing ? save.isPending : preview.isPending)
            }
            onClick={isReviewing ? submit : review}
          >
            {isReviewing ? t("common:save") : t("common:next")}
          </Button>
        </Stack>
      }
    >
      {currentCompensationLoading ? (
        <Skeleton className="h-64 w-full" />
      ) : isReviewing ? (
        preview.data && (
          <EmployeeCompensationPreview
            changeItems={changeItems}
            components={components ?? []}
            lines={currentLines}
            diffs={diffs}
            preview={preview.data}
            missingLineTitles={missingTitles}
            hasCompensationLines={currentLines.length > 0 || originalLines.length > 0}
          />
        )
      ) : (
        <form>
          <Stack>
            {fieldConfig(ApiFieldTypes.effective_from) && (
              <Stack style={{ order: fieldOrder(ApiFieldTypes.effective_from) }} gap="xs">
                <Controller
                  control={control}
                  name="date_effective_from"
                  render={({ field }) => (
                    <Datepicker
                      {...field}
                      required
                      label={fieldTitle(
                        ApiFieldTypes.effective_from,
                        t("common:date_effective_from")
                      )}
                      error={errors.date_effective_from?.message}
                    />
                  )}
                />
                <FieldHint hint={fieldConfig(ApiFieldTypes.effective_from)?.hint} />
                <Controller
                  control={control}
                  name="currency"
                  render={({ field }) => (
                    <CurrencySelect
                      {...field}
                      required
                      label={t("common:currency")}
                      emptyTitle={t("common:select")}
                      searchable
                      isClearable
                      error={errors.currency?.message}
                    />
                  )}
                />
                <FieldHint hint={fieldConfig(ApiFieldTypes.employee_compensation_currency)?.hint} />
              </Stack>
            )}
            <Stack
              style={{ order: fieldOrder(ApiFieldTypes.employee_compensation_lines) }}
              gap="xs"
            >
              <EmployeeCompensationLines
                fields={fields.map((field, index) => ({
                  ...field,
                  ...watched.lines?.[index],
                }))}
                components={components ?? []}
                control={control}
                errors={errors}
                onAdd={addLine}
                onDelete={remove}
                onComponentChange={changeLineComponent}
                onAmountChange={(index) =>
                  void trigger([`lines.${index}.amount_gross`, `lines.${index}.amount_net`])
                }
              />
              <FieldHint hint={fieldConfig(ApiFieldTypes.employee_compensation_lines)?.hint} />
            </Stack>
            {fieldConfig(ApiFieldTypes.employee_compensation_change_reason) && (
              <Stack
                style={{ order: fieldOrder(ApiFieldTypes.employee_compensation_change_reason) }}
                gap="xs"
              >
                <Controller
                  control={control}
                  name="change_reason_id"
                  render={({ field }) => (
                    <Select
                      {...field}
                      label={fieldTitle(
                        ApiFieldTypes.employee_compensation_change_reason,
                        t("core:compensation_change_reason")
                      )}
                      required={isMandatory(ApiFieldTypes.employee_compensation_change_reason)}
                      options={changeReasons}
                      isLoading={changeReasonsLoading}
                      emptyTitle={t("common:select")}
                      searchable
                      isClearable
                    />
                  )}
                />
                <FieldHint
                  hint={fieldConfig(ApiFieldTypes.employee_compensation_change_reason)?.hint}
                />
              </Stack>
            )}
            {fieldConfig(ApiFieldTypes.employee_compensation_comment) && (
              <Stack
                style={{ order: fieldOrder(ApiFieldTypes.employee_compensation_comment) }}
                gap="xs"
              >
                <Textarea
                  label={fieldTitle(
                    ApiFieldTypes.employee_compensation_comment,
                    t("common:add_comment")
                  )}
                  required={isMandatory(ApiFieldTypes.employee_compensation_comment)}
                  error={errors.comment?.message as string | undefined}
                  {...register("comment")}
                />
                <FieldHint hint={fieldConfig(ApiFieldTypes.employee_compensation_comment)?.hint} />
              </Stack>
            )}
            {customFields.map((field) => (
              <Stack
                key={field.id}
                style={{ order: configFields.findIndex((item) => item.id === field.id) }}
              >
                {getFieldByType({
                  field,
                  formHook: form,
                  canView: true,
                  canEdit: true,
                  label: field.title,
                  options: field.options.map((option) => ({
                    id: Number(option.id),
                    title: option.title,
                  })),
                })}
              </Stack>
            ))}
          </Stack>
        </form>
      )}
    </Modal>
  );
}
